import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { business } from "@/data/site";

const links = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "About", href: "#about" },
  { label: "Service Area", href: "#area" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const h = (hash) => (pathname === "/" ? hash : "/" + hash);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#1C1C1C]/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href={h("#top")} data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="h-9 w-9 grid place-items-center bg-[#FF4500] text-[#141414] font-display font-extrabold text-lg">
            D
          </span>
          <span className="font-display font-extrabold tracking-tight text-lg leading-none">
            DUKE&nbsp;SUMMERS
            <span className="block text-[10px] tracking-[0.35em] text-[#FF4500] font-bold">
              MOVING
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={h(l.href)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-neutral-300 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/earn-with-us"
            data-testid="nav-earn-with-us"
            className="text-sm font-medium text-neutral-300 hover:text-white transition-colors"
          >
            Earn With Us
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${business.phone}`}
            data-testid="header-call-btn"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-neutral-200 hover:text-[#FF4500] transition-colors"
          >
            <Phone className="h-4 w-4" strokeWidth={1.75} />
            {business.phone}
          </a>
          <a
            href={h("#quote")}
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center bg-[#FF4500] text-[#141414] font-bold text-sm px-5 py-2.5 hover:bg-[#E03C00] transition-colors"
          >
            Request a Quote
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#1C1C1C]/95 backdrop-blur-xl border-t border-white/10 px-5 py-6 flex flex-col gap-4"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={h(l.href)}
              onClick={() => setOpen(false)}
              className="text-base font-medium text-neutral-200 hover:text-[#FF4500]"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/earn-with-us"
            data-testid="mobile-nav-earn-with-us"
            onClick={() => setOpen(false)}
            className="text-base font-medium text-neutral-200 hover:text-[#FF4500]"
          >
            Earn With Us
          </Link>
          <a
            href={h("#quote")}
            onClick={() => setOpen(false)}
            data-testid="mobile-quote-btn"
            className="mt-2 text-center bg-[#FF4500] text-[#141414] font-bold px-5 py-3"
          >
            Request a Quote
          </a>
        </div>
      )}
    </header>
  );
};
